import React, { memo } from "react";
import { Position } from "@xyflow/react";
import { DeviceNodeData, isNodeCorrelationError } from "../flow-utils";
import CircleButton from "widgets/controls/buttons/circle-button";
import RectangleHandle from "../../handles/rectangle";
import { TbAlertTriangle, TbCircleLetterD } from "react-icons/tb";
import CircleMenu from "../../components/circle-menu";
import { ModelFilterDropdown } from "models/dropdowns/modelFilter";
import { useTranslation } from "react-i18next";
import TooltipHorizon from "widgets/controls/tooltip";

const DeviceNode = (props: { data: DeviceNodeData }) => {
  const { t } = useTranslation();
  const { data } = props;
  const onlineValue = data.data?.info.find(
    (info) => info.name === "online"
  )?.value;
  const isOnline =
    onlineValue === true || onlineValue === "true" || onlineValue === "TRUE";

  const isError = isNodeCorrelationError(data);

  const isHealthy = data.data?.info?.find(
    (info) => info.name === "dataCoverage"
  )?.value
    ? true
    : false;

  const dataCoverageInfo = data.data?.info?.find(
    (info) => info.name === "dataCoverage"
  );

  const hasRawNearStart = dataCoverageInfo?.conditions?.hasRawNearStart
    ? t("generic.true")
    : t("generic.false");
  const hasRawNearEnd = dataCoverageInfo?.conditions?.hasRawNearEnd
    ? t("generic.true")
    : t("generic.false");

  const modelType = ModelFilterDropdown.find((m) =>
    m.value.includes(data.device?.model)
  )?.label;
  return (
    <div
      className={`border-stone-400 min-w-52 rounded-3xl border-2 bg-white px-4 py-2 shadow-lg`}
      style={{ width: data.width, height: data.height }}
      key={data.id}
    >
      {/* Node content */}
      <div className="flex h-full flex-col justify-between p-1">
        <div className="flex justify-between">
          {/* Node main content */}
          <div>
            <div className="flex items-center gap-2">
              <div
                className="truncate text-lg font-bold text-primary"
                style={{ maxWidth: data.width - 10 }}
              >
                {data.name}
              </div>
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  isOnline ? "bg-green-500" : "bg-red-500"
                }`}
              />
            </div>
            {data.device && (
              <div
                className="truncate text-xs text-secondary"
                style={{ maxWidth: data.width - 10 }}
              >
                SNR: {data.device.snr}
              </div>
            )}
            {data.data && (
              <>
                <div className="truncate text-xs font-bold text-primary">
                  {t("generic.usage")}: {data.data.usage}{" "}
                  {t(`generic.unit`, { context: modelType.toLowerCase() })}
                </div>
                <div
                  className={`truncate text-xs font-bold ${
                    isError ? "text-red-500" : "text-primary"
                  }`}
                >
                  CORRELATION:{" "}
                  {
                    data.data.info.find((info) => info.name === "correlation")
                      ?.value
                  }
                </div>
              </>
            )}
          </div>

          {/* Menu buttons */}
          <div className="flex">
            <CircleMenu data={data} />
            {!isHealthy ? (
              <TooltipHorizon
                extra="whitespace-normal"
                placement="top"
                trigger={
                  <TbAlertTriangle className="text-3xl text-yellow-500" />
                }
                content={
                  <span className="block whitespace-normal break-words text-sm leading-snug">
                    <div>
                      {t("generic.has.raw.near.start")}: {hasRawNearStart}
                    </div>
                    <div>
                      {t("generic.has.raw.near.end")}: {hasRawNearEnd}
                    </div>
                  </span>
                }
              />
            ) : (
              <TbCircleLetterD className="text-3xl text-orange-500" />
            )}
          </div>
        </div>
        {/* Bottom buttons - only in builder mode */}
        {data.onDelete && (
          <div className="flex justify-end">
            <CircleButton
              label="REMOVE"
              size="xs"
              color="red"
              onClick={() => {
                data.onDelete?.(data.id);
              }}
            />
          </div>
        )}
      </div>

      {/* Target handle - flow in */}
      <RectangleHandle
        type="target"
        position={data.targetPosition ?? Position.Left}
        width={data.width}
        height={data.height}
      />

      {/* Source handle - flow out */}
      <RectangleHandle
        type="source"
        position={data.sourcePosition ?? Position.Right}
        width={data.width}
        height={data.height}
      />
    </div>
  );
};

export default memo(DeviceNode);
